import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  FormControl,
  FormLabel,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import HPInput from "@components/common/Input";
import {
  STACKED_CUSTOM_PALETTE,
  type StackedSegmentColorEntry,
} from "@utils/stacked-bar-utils";

// Stacked bar palettes are multi-color sequences (one color per segment) plus
// a per-segment Custom option. Single-color palettes are intentionally
// omitted — they would render every segment in the same color.
const PALETTE_OPTIONS: { value: string; gradient: string }[] = [
  {
    value: "Multicolor",
    gradient:
      "linear-gradient(90deg, #57bfe7 0%, #57dfbe 25%, #ffb09c 50%, #a1a7df 75%, #c36179 100%)",
  },
  {
    value: "Gradient",
    gradient: "linear-gradient(90deg, #019EDB 0%, #00CF9C 100%)",
  },
  {
    value: "Grayscale",
    gradient: "linear-gradient(90deg, #f0f0f0 0%, #404040 100%)",
  },
  {
    value: STACKED_CUSTOM_PALETTE,
    gradient:
      "linear-gradient(90deg, #e57373 0%, #81c784 33%, #64b5f6 66%, #ffd54f 100%)",
  },
];

interface StackedStylingSectionProps {
  title: string;
  subtitle: string;
  colorPalette: string;
  segmentColorEntries: StackedSegmentColorEntry[];
  manualColors: Record<string, string>;
  onTitleChange: (value: string) => void;
  onSubtitleChange: (value: string) => void;
  onColorPaletteChange: (value: string) => void;
  onSegmentColorChange: (id: string, color: string | null) => void;
}

/**
 * Styling & Customization for stacked bar: Chart Title, Subtitle and Color
 * Palette (multi-color sequences + Custom per-segment).
 */
const StackedStylingSection: React.FC<StackedStylingSectionProps> = ({
  title,
  subtitle,
  colorPalette,
  segmentColorEntries,
  manualColors,
  onTitleChange,
  onSubtitleChange,
  onColorPaletteChange,
  onSegmentColorChange,
}) => {
  // Tracks each segment's color before the current (unconfirmed) edit, so
  // Cancel can revert — same pattern as grouped bar's per-series picker.
  const [pendingOriginals, setPendingOriginals] = useState<
    Record<string, string | null>
  >({});

  useEffect(() => {
    if (colorPalette !== STACKED_CUSTOM_PALETTE) setPendingOriginals({});
  }, [colorPalette]);

  const isCustom = colorPalette === STACKED_CUSTOM_PALETTE;

  return (
    <Accordion
      defaultExpanded
      sx={{ boxShadow: "none", border: "1px solid #eee" }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Styling & Customization
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <HPInput
            label="Chart title"
            value={title}
            onChange={(e) => onTitleChange(e.target.value)}
          />

          <HPInput
            label="Subtitle"
            value={subtitle}
            onChange={(e) => onSubtitleChange(e.target.value)}
            placeholder="Optional"
          />

          {/* Color Palette */}
          <FormControl fullWidth size="small">
            <FormLabel className="text-[12px] text-[#262626]">
              Color palette
            </FormLabel>
            <Select
              value={colorPalette}
              onChange={(e) => onColorPaletteChange(e.target.value)}
              renderValue={(value) => {
                const opt = PALETTE_OPTIONS.find((o) => o.value === value);
                return (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      justifyContent: "space-between",
                      flexDirection: "row-reverse",
                    }}
                  >
                    <Box
                      sx={{
                        width: 24,
                        height: 12,
                        borderRadius: "16px",
                        background: opt?.gradient,
                      }}
                    />
                    {value}
                  </Box>
                );
              }}
            >
              {PALETTE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 16,
                        height: 16,
                        borderRadius: "50%",
                        background: option.gradient,
                      }}
                    />
                    {option.value}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Per-segment color pickers (Custom palette only) */}
          {isCustom && segmentColorEntries.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {segmentColorEntries.map((entry) => {
                const currentColor =
                  manualColors[entry.id] || entry.fallbackColor;
                const isDirty = entry.id in pendingOriginals;
                return (
                  <Box
                    key={entry.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        fontSize: "12px",
                      }}
                    >
                      {entry.displayName}
                    </Typography>
                    <input
                      type="color"
                      value={currentColor}
                      onChange={(e) => {
                        if (!(entry.id in pendingOriginals)) {
                          setPendingOriginals((prev) => ({
                            ...prev,
                            [entry.id]: manualColors[entry.id] ?? null,
                          }));
                        }
                        onSegmentColorChange(entry.id, e.target.value);
                      }}
                      style={{
                        width: 32,
                        height: 24,
                        cursor: "pointer",
                        border: isDirty ? "2px solid #1976d2" : "1px solid #ccc",
                        borderRadius: 4,
                        padding: 0,
                      }}
                    />
                    {isDirty && (
                      <>
                        <IconButton
                          size="small"
                          color="primary"
                          title="Apply color"
                          onClick={() =>
                            setPendingOriginals((prev) => {
                              const next = { ...prev };
                              delete next[entry.id];
                              return next;
                            })
                          }
                        >
                          <CheckIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          title="Cancel"
                          onClick={() => {
                            onSegmentColorChange(
                              entry.id,
                              pendingOriginals[entry.id],
                            );
                            setPendingOriginals((prev) => {
                              const next = { ...prev };
                              delete next[entry.id];
                              return next;
                            });
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default StackedStylingSection;
